import { supabase } from './supabase';

export interface CustomCommand {
  id: string;
  name: string;
  response: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
  // Legacy fields for backward compatibility
  user_id?: string;
  trigger_command?: string;
  response_message?: string;
  case_sensitive?: boolean;
  requires_prefix?: boolean;
  usage_count?: number;
  last_used_at?: string;
  last_used_by?: string;
}

export interface CreateCustomCommandInput {
  name?: string;
  response?: string;
  enabled?: boolean;
  // Legacy fields for backward compatibility
  trigger_command?: string;
  response_message?: string;
  case_sensitive?: boolean;
  requires_prefix?: boolean;
}

// Rumble chat limits and command constraints
export const COMMAND_LIMITS = {
  MAX_TRIGGER_LENGTH: 50,
  MAX_RESPONSE_LENGTH: 500,
  MIN_TRIGGER_LENGTH: 1,
};

// Available template variables
export const TEMPLATE_VARIABLES = {
  '{senderUsername}': 'Username of the person who triggered the command',
  '{currentTime}': 'Current time (HH:MM format)',
  '{currentDate}': 'Current date (MM/DD/YYYY format)',
  '{currentDateTime}': 'Current date and time',
  '{randomNumber}': 'Random number between 1-100',
  '{commandTrigger}': 'The command that was triggered',
  '{botUsername}': 'The bot\'s username (d0rk)',
  '{userCount}': 'Number of users in chat (if available)',
  '{uptime}': 'How long the stream has been live',
} as const;

/**
 * Process template variables in a response message
 */
export function processTemplateVariables(
  message: string, 
  context: {
    senderUsername?: string;
    commandTrigger?: string;
    userCount?: number;
    uptime?: string;
  } = {}
): string {
  let processedMessage = message;
  
  const now = new Date();
  
  // Replace template variables
  const replacements: Record<string, string> = {
    '{senderUsername}': context.senderUsername || 'Unknown',
    '{currentTime}': now.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    }),
    '{currentDate}': now.toLocaleDateString('en-US'),
    '{currentDateTime}': now.toLocaleString('en-US'),
    '{randomNumber}': Math.floor(Math.random() * 100 + 1).toString(),
    '{commandTrigger}': context.commandTrigger || '',
    '{botUsername}': 'd0rk',
    '{userCount}': context.userCount?.toString() || '0',
    '{uptime}': context.uptime || 'Unknown',
  };
  
  // Replace all variables
  Object.entries(replacements).forEach(([variable, value]) => {
    processedMessage = processedMessage.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  
  return processedMessage;
}

/**
 * Validate custom command input
 */
export function validateCustomCommand(input: CreateCustomCommandInput, mentionWord: string = 'dork'): string[] {
  const errors: string[] = [];

  // Support both new and legacy field names
  const commandName = input.name || input.trigger_command;
  const commandResponse = input.response || input.response_message;

  // Name validation
  if (!commandName?.trim()) {
    errors.push('Command name is required');
  } else {
    const name = commandName.trim();

    if (name.length < COMMAND_LIMITS.MIN_TRIGGER_LENGTH) {
      errors.push('Command name must be at least 1 character');
    }

    if (name.length > COMMAND_LIMITS.MAX_TRIGGER_LENGTH) {
      errors.push(`Command name must be ${COMMAND_LIMITS.MAX_TRIGGER_LENGTH} characters or less`);
    }

    // Remove ! prefix for validation
    const cleanName = name.startsWith('!') ? name.substring(1) : name;

    if (!/^[a-zA-Z0-9_-]+$/.test(cleanName)) {
      errors.push('Command name can only contain letters, numbers, hyphens, and underscores');
    }

    if (/^\d+$/.test(cleanName)) {
      errors.push('Command name cannot be only numbers');
    }

    // Check for reserved mention word
    if (cleanName.toLowerCase() === mentionWord.toLowerCase()) {
      errors.push(`"${mentionWord}" is a reserved keyword and cannot be used as a command name`);
    }
  }

  // Response validation
  if (!commandResponse?.trim()) {
    errors.push('Response message is required');
  } else if (commandResponse.length > COMMAND_LIMITS.MAX_RESPONSE_LENGTH) {
    errors.push(`Response message must be ${COMMAND_LIMITS.MAX_RESPONSE_LENGTH} characters or less`);
  } else if (commandResponse.toLowerCase().includes(mentionWord.toLowerCase())) {
    errors.push(`"${mentionWord}" is a reserved keyword and cannot be used in response messages`);
  }

  return errors;
}

/**
 * Get all custom commands for the current user
 */
export async function getCustomCommands(): Promise<CustomCommand[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to view custom commands');
  }
  
  const { data, error } = await supabase.rpc('get_my_custom_commands');
  
  if (error) {
    console.error('Error fetching custom commands:', error);
    throw new Error('Failed to load custom commands');
  }
  
  // Convert JSONB array to CustomCommand array
  return Array.isArray(data) ? data : [];
}

/**
 * Create a new custom command
 */
export async function createCustomCommand(input: CreateCustomCommandInput): Promise<CustomCommand> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to create custom commands');
  }

  // Fetch mention_word from user_rumble_settings
  const { data: rumbleSettings } = await supabase
    .from('user_rumble_settings')
    .select('mention_word')
    .eq('user_id', user.id)
    .single();

  const mentionWord = rumbleSettings?.mention_word || 'dork';

  // Validate input with mention_word
  const errors = validateCustomCommand(input, mentionWord);
  if (errors.length > 0) {
    throw new Error(errors.join(', '));
  }

  const commandName = (input.name || input.trigger_command || '').trim();
  const commandResponse = (input.response || input.response_message || '').trim();

  const { data, error } = await supabase.rpc('add_custom_command', {
    command_name: commandName,
    command_response: commandResponse
  });

  if (error) {
    console.error('Error creating custom command:', error);
    // Check for specific error types
    if (error.message?.includes('NO_RUMBLE_API')) {
      const customError = new Error('NO_RUMBLE_API');
      (customError as any).isRumbleApiError = true;
      throw customError;
    }
    if (error.message?.includes('NOT_WHITELISTED')) {
      const customError = new Error('NOT_WHITELISTED');
      (customError as any).isWhitelistError = true;
      throw customError;
    }
    throw new Error(error.message || 'Failed to create custom command');
  }

  return data;
}

/**
 * Update a custom command
 */
export async function updateCustomCommand(
  id: string,
  updates: Partial<CreateCustomCommandInput & { enabled: boolean }>
): Promise<CustomCommand> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to update custom commands');
  }

  // If updating name or response, validate
  if (updates.name || updates.response) {
    // Fetch mention_word from user_rumble_settings
    const { data: rumbleSettings } = await supabase
      .from('user_rumble_settings')
      .select('mention_word')
      .eq('user_id', user.id)
      .single();

    const mentionWord = rumbleSettings?.mention_word || 'dork';

    const merged = {
      name: updates.name || '',
      response: updates.response || '',
    };

    const errors = validateCustomCommand(merged, mentionWord);
    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }
  }

  const { data, error } = await supabase.rpc('update_custom_command', {
    command_id: id,
    command_name: updates.name?.trim(),
    command_response: updates.response?.trim(),
    command_enabled: updates.enabled
  });

  if (error) {
    console.error('Error updating custom command:', error);
    throw new Error(error.message || 'Failed to update custom command');
  }

  return data;
}

/**
 * Delete a custom command
 */
export async function deleteCustomCommand(id: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to delete custom commands');
  }
  
  const { error } = await supabase.rpc('delete_custom_command', {
    command_id: id
  });
  
  if (error) {
    console.error('Error deleting custom command:', error);
    throw new Error(error.message || 'Failed to delete custom command');
  }
}

/**
 * Toggle enabled status of a custom command
 */
export async function toggleCustomCommand(id: string, enabled: boolean): Promise<CustomCommand> {
  return updateCustomCommand(id, { enabled });
}

/**
 * Get template variable options for the form
 */
export function getTemplateVariables() {
  return Object.entries(TEMPLATE_VARIABLES).map(([variable, description]) => ({
    variable,
    description,
  }));
}